// utils/smartRebalancing.js
import moment from 'moment';

/**
 * Smart job selection for rebalancing
 * Prioritizes jobs based on multiple factors for optimal transfer
 */
export const getSmartJobSuggestions = (fromCrewJobs, fromCrewMetrics, toCrewMetrics) => {
  if (!fromCrewJobs || fromCrewJobs.length === 0) return [];

  // Score each job for transfer suitability
  const scoredJobs = fromCrewJobs.map(job => {
    let score = 0;
    const reasons = [];

    // 1. Prioritize longer jobs (bigger impact on overtime reduction)
    if (job.estimatedHours > 2) {
      score += job.estimatedHours * 10;
      reasons.push(`Long job (${job.estimatedHours.toFixed(1)}h)`);
    }

    // 2. Prioritize jobs on overloaded days
    const jobDay = job.day;
    const dayHours = fromCrewMetrics.workload.dailyHours[jobDay] || 0;
    if (dayHours > 8) {
      score += (dayHours - 8) * 20;
      reasons.push(`Heavy day (${dayHours.toFixed(1)}h)`);
    }

    // 3. Prioritize geographically distant jobs (if we have location data)
    if (job.distanceFromBase && job.distanceFromBase > 10) {
      score += job.distanceFromBase * 2;
      reasons.push(`Far location (${job.distanceFromBase}mi)`);
    }

    // 4. Check if target crew has capacity on that day
    const targetDayHours = toCrewMetrics.workload.dailyHours[jobDay] || 0;
    const capacityAvailable = 8 - targetDayHours;
    if (capacityAvailable >= job.estimatedHours) {
      score += 30;
      reasons.push('Good fit for target crew');
    } else if (capacityAvailable > 0) {
      score += 10;
      reasons.push('Partial fit');
    }

    // 5. Prioritize jobs that aren't time-sensitive
    if (job.frequency === 'Bi-Weekly' || job.frequency === 'Monthly') {
      score += 15;
      reasons.push('Flexible frequency');
    }

    return {
      ...job,
      transferScore: score,
      transferReasons: reasons
    };
  });

  // Sort by score and return top suggestions
  return scoredJobs
    .sort((a, b) => b.transferScore - a.transferScore)
    .slice(0, 10); // Limit to top 10 suggestions
};

/**
 * Calculate optimal job combinations for target hours
 * Uses a greedy algorithm to find best combination
 */
export const getOptimalJobCombination = (jobs, targetHours, maxJobs = 5) => {
  if (!jobs || jobs.length === 0) return [];

  // Sort jobs by efficiency (score per hour)
  const sortedJobs = [...jobs].sort((a, b) => {
    const scorePerHourA = a.transferScore / a.estimatedHours;
    const scorePerHourB = b.transferScore / b.estimatedHours;
    return scorePerHourB - scorePerHourA;
  });

  const selected = [];
  let totalHours = 0;

  // Greedy selection
  for (const job of sortedJobs) {
    if (selected.length >= maxJobs) break;
    if (totalHours + job.estimatedHours <= targetHours * 1.1) { // Allow 10% overage
      selected.push(job);
      totalHours += job.estimatedHours;
    }
  }

  // If we're significantly under target, try to add one more small job
  if (totalHours < targetHours * 0.8) {
    const remainingJobs = sortedJobs.filter(j => !selected.includes(j));
    const smallJob = remainingJobs.find(j => 
      j.estimatedHours <= (targetHours - totalHours) * 1.2
    );
    if (smallJob) {
      selected.push(smallJob);
      totalHours += smallJob.estimatedHours;
    }
  }

  return {
    jobs: selected,
    totalHours,
    efficiency: totalHours / targetHours
  };
};

/**
 * Validate if a job transfer is safe and beneficial
 */
export const validateJobTransfer = (job, fromCrew, toCrew, fromMetrics, toMetrics) => {
  const warnings = [];
  const blockers = [];

  // Check if target crew has required skills (if applicable)
  if (job.requiredSkills && toCrew.skills) {
    const missingSkills = job.requiredSkills.filter(skill => 
      !toCrew.skills.includes(skill)
    );
    if (missingSkills.length > 0) {
      blockers.push(`Target crew missing skills: ${missingSkills.join(', ')}`);
    }
  }

  // Check if transfer would overload target crew
  const targetNewHours = toMetrics.workload.weeklyHours + job.estimatedHours;
  if (targetNewHours > 45) {
    warnings.push(`Would put target crew at ${targetNewHours.toFixed(1)}h`);
  }
  if (targetNewHours > 50) {
    blockers.push('Would create excessive overtime for target crew');
  }

  // Check geographic efficiency
  if (job.distanceFromBase && toMetrics.geographic.avgMilesPerStop) {
    const currentAvg = toMetrics.geographic.avgMilesPerStop;
    if (job.distanceFromBase > currentAvg * 2) {
      warnings.push('Job is far from target crew territory');
    }
  }

  // Check day capacity
  const jobDay = job.day;
  const targetDayHours = toMetrics.workload.dailyHours[jobDay] || 0;
  if (targetDayHours + job.estimatedHours > 10) {
    warnings.push(`${jobDay} would have ${(targetDayHours + job.estimatedHours).toFixed(1)}h`);
  }

  return {
    isValid: blockers.length === 0,
    warnings,
    blockers,
    recommendation: blockers.length === 0 ? 
      (warnings.length === 0 ? 'Optimal transfer' : 'Transfer with caution') :
      'Do not transfer'
  };
};

/**
 * Generate a complete rebalancing plan
 */
export const generateRebalancingPlan = (crews, crewMetrics, targetDate) => {
  const plans = [];

  // Find all overworked crews
  const overworkedCrews = Object.values(crewMetrics).filter(
    metric => metric.workload.overtime > 4
  );

  // Find all underutilized crews
  const availableCrews = Object.values(crewMetrics).filter(
    metric => metric.workload.utilization < 70 && metric.workload.weeklyHours < 35
  );

  // Generate transfer plans
  overworkedCrews.forEach(fromMetrics => {
    availableCrews.forEach(toMetrics => {
      // Skip if same crew
      if (fromMetrics.crewId === toMetrics.crewId) return;

      // Calculate how many hours to transfer
      const hoursToTransfer = Math.min(
        fromMetrics.workload.overtime,
        40 - toMetrics.workload.weeklyHours
      );

      if (hoursToTransfer > 2) {
        plans.push({
          fromCrew: fromMetrics,
          toCrew: toMetrics,
          targetHours: hoursToTransfer,
          potentialImpact: {
            overtimeReduction: hoursToTransfer,
            costSavings: Math.round(hoursToTransfer * 35 * 0.5), // Time-and-a-half
            fromCrewNewHours: fromMetrics.workload.weeklyHours - hoursToTransfer,
            toCrewNewHours: toMetrics.workload.weeklyHours + hoursToTransfer
          }
        });
      }
    });
  });

  // Sort plans by impact
  return plans.sort((a, b) => 
    b.potentialImpact.overtimeReduction - a.potentialImpact.overtimeReduction
  );
};